import { useState } from "react"
import Markdown from "react-markdown"
import styled from "styled-components"

import { colors, spacing, TextArea } from "@minuthq/meatball-ui-react"

import { TabPanel } from "src/ui/Tabs/TabPanel"
import { ITabsProps, Tabs } from "src/ui/Tabs/Tabs"
import { textAreaValueToMarkdown } from "src/utils/markdownUtils"

/**
 * Very simple text editor that handles Markdown
 * Currently it only support line breaks
 *
 * The idea is that the caller only knows about Markdown.
 * The editor itself makes sure to convert the Markdown to a textarea-friendly format and vice versa.
 *
 * If we see a similar pattern used in more places it might be worth creating a component in Meatball UI
 */
export function ParadiseCreateQuoteNoteField({
  label,
  placeholder,
  markdownValue,
  onChange,
  required = false,
  requiredIndicator = false,
}: {
  label?: string
  placeholder?: string
  markdownValue: string
  onChange: (value: string) => void
  required?: boolean
  requiredIndicator?: boolean
}) {
  const [textAreaValue, setTextAreaValue] = useState(() => {
    return markdownToTextAreaValue(markdownValue)
  })

  function handleChange(value: string) {
    setTextAreaValue(value)

    const newMarkdownValue = textAreaValueToMarkdown(value)
    onChange(newMarkdownValue)
  }

  const tabs: ITabsProps["labels"] = [
    {
      id: "editor",
      label: "Editor",
    },
    {
      id: "preview",
      label: "Preview",
    },
  ]

  return (
    <div>
      <Tabs labels={tabs}>
        <StyledTab>
          <TextArea
            label={label}
            placeholder={placeholder}
            value={textAreaValue}
            onChange={handleChange}
            required={required}
            requiredIndicator={requiredIndicator}
          />
        </StyledTab>
        <StyledTab>
          <MarkdownWrapper>
            <Markdown>{markdownValue}</Markdown>
          </MarkdownWrapper>
        </StyledTab>
      </Tabs>
    </div>
  )
}

const StyledTab = styled(TabPanel)`
  margin-block: ${spacing.S};
`

const MarkdownWrapper = styled.div`
  padding: ${spacing.S};
  background-color: ${colors.accentPrimaryLight};
  border-radius: 0.5rem;
`

/**
 * Converts a Markdown string into a textarea-friendly string.
 * It converts forced line breaks (exactly two spaces followed by a newline) into a single newline.
 *
 * @param markdown - The Markdown formatted string.
 * @returns A version that is friendlier for the textarea.
 */
function markdownToTextAreaValue(markdown: string): string {
  // Replace exactly two spaces followed by newline with newline.
  return markdown.replace(/ {2}\n/g, "\n")
}
